import React, { useEffect, useContext } from 'react'
import classNames from 'classnames'
import { Button, FancyModal } from '@deal/components'
import { EngagementChannelCategoryFragment } from '#src/generated/graphql'
import ContentfulQuery from '#src/containers/Contentful'
import { AnalyticsContext } from '#src/containers/Analytics'
import { ContentfulResponse } from './schema'
import styles from './styles.css'
import BackgroundImage from '#src/components/BackgroundImage'

interface Props {
  contentfulVariationSlug: string
  isModalOpen: boolean
  category: EngagementChannelCategoryFragment
  onDismissModal: (trackDismiss: boolean, setCookie: boolean) => void
  onSubmitModal: (categorySlug: string) => void
}

const DefaultVariation: React.SFC<Props> = ({
  category,
  isModalOpen,
  contentfulVariationSlug,
  onSubmitModal,
  onDismissModal
}) => {
  const analytics = useContext(AnalyticsContext)
  const fullPageTakeoverImage = category.contentAssets.fullPageTakeover
    ? category.contentAssets.fullPageTakeover.imageUrl
    : null

  useEffect(() => {
    analytics?.events.engagementChannelViewed({
      engagement_channel: 'full-page-takeover',
      variation: contentfulVariationSlug,
      category: category.slug
    })
  })

  return (
    <ContentfulQuery template="engagementModal" variation={contentfulVariationSlug}>
      {({ loading, content }: ContentfulResponse) => {
        if (loading) {
          return null
        } else if (content) {
          return (
            <FancyModal
              isOpen={isModalOpen}
              onRequestClose={() => onDismissModal(true, true)}
              className={styles.modal}
              modalContentClassName={styles.modalContent}
            >
              <BackgroundImage
                containerClassName={styles.background}
                size="100vw"
                sizeSm="800px"
                src={fullPageTakeoverImage || ''}
              >
                <div className={styles.gradientMask} />
                <div className={classNames(styles.content, styles.gradientContent)}>
                  <div className={styles.headerSmall}>{content.header}</div>
                  <div className={styles.actionSmall}>
                    <Button size="medium" onClick={() => onSubmitModal(category.slug)}>
                      {content.buttonText ? content.buttonText : 'Get started'}
                    </Button>

                    <Button
                      size="medium"
                      onClick={() => onDismissModal(true, true)}
                      className={classNames(styles.buttonClearBlack, styles.actionDismiss)}
                    >
                      {content.dismissButtonText ? content.dismissButtonText : 'Maybe later'}
                    </Button>
                  </div>
                  <div className={styles.stepsSmall}>
                    {content.questionsTitle && (
                      <div className={styles.stepsTitle}>{content.questionsTitle}</div>
                    )}
                    {content.questions.map((question, idx) => (
                      <div className={styles.step} key={idx}>
                        <div className={styles.stepHeaderSmall}>{question.title}</div>
                        <div className={styles.stepBodySmall}>{question.subtext}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </BackgroundImage>
            </FancyModal>
          )
        }

        /**
         * In the event there isn't content set up for the engagement modal for this
         * category we want to set the cookie. We don't want the module to popup if
         * they switch to a category that has a contentful entry.
         */
        onDismissModal(false, false)

        return null
      }}
    </ContentfulQuery>
  )
}

export default DefaultVariation
